import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useLocale } from '../../contexts/LocaleContext';
import { Button } from '../ui/button';
import { CancelButton } from '../ui/cancel-button';
import { AdminAccessDenied } from './AdminAccessDenied';
import { ADMIN_PAGE_CLASSNAME, ADMIN_STATS_ROW_CLASSNAME, ADMIN_STAT_CARD_CLASSNAME, ADMIN_STAT_LABEL_CLASSNAME, ADMIN_STAT_VALUE_CLASSNAME, AdminErrorAlert, AdminEmptyState, AdminLoadingState, AdminTableWrap } from './admin-ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface ChapterAnalysisTracking {
  id: string;
  userId: string;
  userEmail?: string;
  documentId: string | null;
  documentTitle?: string;
  chapterTitle?: string;
  createdAt: string;
}

function getAuthHeaders(token: string | null, masterKey: string | null): Record<string, string> {
  if (masterKey) {
    return {};
  }
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

function buildChaptersUrl(documentId: string, masterKey: string | null): string {
  const base = `${API_BASE}/api/documents/${documentId}/chapters`;
  return masterKey ? `${base}?masterKey=${encodeURIComponent(masterKey)}` : base;
}

function buildAddChapterUrl(documentId: string, masterKey: string | null): string {
  const base = `${API_BASE}/api/documents/${documentId}/chapters`;
  return masterKey ? `${base}?masterKey=${encodeURIComponent(masterKey)}` : base;
}

export const ChapterAnalyses: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [chapterAnalyses, setChapterAnalyses] = useState<ChapterAnalysisTracking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addToOutlineOpen, setAddToOutlineOpen] = useState(false);
  const [addToOutlineTitles, setAddToOutlineTitles] = useState<Record<string, string>>({});
  const [addingToOutline, setAddingToOutline] = useState(false);
  const [addToOutlineError, setAddToOutlineError] = useState<string | null>(null);

  const getDisplayTitle = (title: string | undefined): string => {
    if (!title || title === '-') {
      return '-';
    }
    if (title === 'Document') {
      return t('document_chapter_title');
    }
    return title;
  };

  const selectedWithDocument = chapterAnalyses.filter(
    (ca) => selectedIds.has(ca.id) && ca.documentId
  );

  const toggleSelection = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = (): void => {
    const selectable = chapterAnalyses.filter((ca) => ca.documentId);
    if (selectedIds.size === selectable.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectable.map((ca) => ca.id)));
    }
  };

  const openAddToOutlineDialog = (): void => {
    const initial: Record<string, string> = {};
    selectedWithDocument.forEach((ca) => {
      initial[ca.id] = ca.chapterTitle ?? '';
    });
    setAddToOutlineTitles(initial);
    setAddToOutlineError(null);
    setAddToOutlineOpen(true);
  };

  const addToOutline = async (): Promise<void> => {
    const entries = selectedWithDocument.map((ca) => ({
      ...ca,
      title: (addToOutlineTitles[ca.id] ?? ca.chapterTitle ?? '').trim(),
    }));
    const missing = entries.filter((e) => !e.title);
    if (missing.length > 0) {
      setAddToOutlineError(t('chapter_title_placeholder'));
      return;
    }
    setAddingToOutline(true);
    setAddToOutlineError(null);
    const headers = getAuthHeaders(token, masterKey);
    try {
      const byDocument = new Map<string | null, typeof entries>();
      entries.forEach((e) => {
        if (!e.documentId) return;
        let list = byDocument.get(e.documentId);
        if (!list) {
          list = [];
          byDocument.set(e.documentId, list);
        }
        list.push(e);
      });
      for (const [docId, list] of byDocument) {
        if (!docId || list.length === 0) continue;
        const chaptersRes = await fetch(buildChaptersUrl(docId, masterKey), { headers });
        if (!chaptersRes.ok) {
          throw new Error('Failed to load chapters');
        }
        const chaptersData = await chaptersRes.json();
        const chaptersByLevel = (chaptersData.chaptersByLevel ?? {}) as Record<number, Array<{ order?: number }>>;
        let maxOrder = 0;
        Object.values(chaptersByLevel).forEach((chapters) => {
          (chapters ?? []).forEach((ch) => {
            const o = ch.order ?? 0;
            if (o > maxOrder) maxOrder = o;
          });
        });
        for (let i = 0; i < list.length; i++) {
          const item = list[i];
          const order = maxOrder + 1 + i;
          const body = { order, title: item.title };
          const res = await fetch(buildAddChapterUrl(docId, masterKey), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...headers },
            body: JSON.stringify(body),
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error ?? 'Failed to add chapter');
          }
        }
      }
      setAddToOutlineOpen(false);
      setSelectedIds(new Set());
    } catch (err) {
      setAddToOutlineError(err instanceof Error ? err.message : 'Failed to add to outline');
    } finally {
      setAddingToOutline(false);
    }
  };

  useEffect(() => {
    const fetchChapterAnalyses = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/chapter-analyses?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/chapter-analyses`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch chapter analyses');
        }

        const data = await response.json();
        setChapterAnalyses(data.chapterAnalysesTracking || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching chapter analyses:', err);
        setError(t('failed_to_load_chapter_analyses'));
      } finally {
        setLoading(false);
      }
    };

    fetchChapterAnalyses();
  }, [token, user, masterKey, t]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  const totalCount = chapterAnalyses.length;

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        {error && (
          <div className="mb-8">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        <div className={`${ADMIN_STATS_ROW_CLASSNAME} items-center`}>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>
              {t('total')}
            </div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{totalCount}</div>
          </div>
          <Button
            onClick={openAddToOutlineDialog}
            disabled={selectedWithDocument.length === 0}
            variant="default"
          >
            {t('add_to_chapter_outline')}
          </Button>
        </div>

        {chapterAnalyses.length === 0 ? (
          <AdminEmptyState>{t('no_chapter_analyses_registered')}</AdminEmptyState>
        ) : (
          <AdminTableWrap>
              <table className="admin-data-table" style={{ minWidth: '1000px' }}>
                <thead>
                  <tr>
                    <th className="w-11 text-center">
                      <input
                        type="checkbox"
                        checked={
                          chapterAnalyses.some((ca) => ca.documentId) &&
                          selectedWithDocument.length === chapterAnalyses.filter((ca) => ca.documentId).length
                        }
                        onChange={toggleSelectAll}
                        aria-label={t('actions')}
                      />
                    </th>
                    <th>
                      {t('user')}
                    </th>
                    <th>
                      {t('document')}
                    </th>
                    <th>
                      {t('document_title')}
                    </th>
                    <th>
                      {t('chapters')}
                    </th>
                    <th>
                      {t('date')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {chapterAnalyses.map((ca) => (
                    <tr
                      key={ca.id}
                      onMouseLeave={(evt) => {
                        (evt.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                      }}
                    >
                      <td className="w-11 text-center">
                        {ca.documentId ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(ca.id)}
                            onChange={() => toggleSelection(ca.id)}
                            aria-label={t('actions')}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {ca.userEmail ? (
                          <Link
                            to={`/admin/users/${ca.userId}`}
                            className="admin-table-link"
                          >
                            {ca.userEmail}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {ca.documentId ? (
                          <Link
                            to={`/documents/${ca.documentId}`}
                            className="admin-table-link"
                          >
                            {ca.documentId.substring(0, 8)}...
                          </Link>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td style={{ maxWidth: '300px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ca.documentTitle || '-'}>
                          {ca.documentTitle || '-'}
                        </div>
                      </td>
                      <td style={{ maxWidth: '300px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ca.chapterTitle || '-'}>
                          {getDisplayTitle(ca.chapterTitle)}
                        </div>
                      </td>
                      <td className="admin-table-cell-muted">
                        {new Date(ca.createdAt).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </AdminTableWrap>
        )}

        <Dialog open={addToOutlineOpen} onOpenChange={setAddToOutlineOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('add_to_chapter_outline')}</DialogTitle>
            </DialogHeader>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {addToOutlineError && (
                <div style={{ color: 'var(--destructive)', fontSize: '0.875rem' }}>
                  {addToOutlineError}
                </div>
              )}
              {selectedWithDocument.map((ca, index) => (
                <div
                  key={ca.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
                    {t('document_title')}: {ca.documentTitle ?? ca.documentId ?? '-'}
                  </div>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('chapter_title_placeholder')} *
                  </label>
                  <input
                    type="text"
                    value={addToOutlineTitles[ca.id] ?? ''}
                    onChange={(e) =>
                      setAddToOutlineTitles((prev) => ({ ...prev, [ca.id]: e.target.value }))
                    }
                    placeholder={t('chapter_title_placeholder')}
                    autoFocus={index === 0}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                    }}
                  />
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                <CancelButton onClick={() => setAddToOutlineOpen(false)} disabled={addingToOutline}>
                  {t('cancel')}
                </CancelButton>
                <Button onClick={() => void addToOutline()} disabled={addingToOutline}>
                  {addingToOutline ? t('adding') : t('add_chapter')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

